"use client";

/**
 * The two-step restore, as a hook.
 *
 * The confirmation is a *moment* modal on the shared global stack, and the flow
 * has four outcomes that each look different on screen:
 *
 *   plan   -> the confirmation fills in
 *   apply  -> success (toast) and the dialog closes
 *   apply  -> the route answered with a PLAN instead of applying: treat it
 *             exactly like a hash mismatch and make the user confirm again
 *   apply  -> 409 (`AuditPlanStaleError`): the record moved while the dialog was
 *             open. NEVER retried silently — re-fetch the plan and say it changed
 *
 * Lives beside the feed rather than inside it because the feed's other job (the
 * paged list) shares nothing with this but the entity id.
 */

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { isRestoreApplied } from "@/lib/audit/api-contract";
import type {
  AuditEventDto,
  AuditableEntityType,
  RestorePlanResponse,
} from "@/lib/audit/api-contract";
import { AuditPlanStaleError, useAuditRestore } from "@/lib/hooks/api/useEntityAuditHistory";

import { errorMessageOf, restoreCautionText, restoreSummaryLabel } from "./restore-summary";

export interface UseAuditRestoreFlowOptions {
  entityType: AuditableEntityType;
  entityId: string | null;
  /** False while the surface is not being looked at — closes a pending dialog. */
  active: boolean;
  /** Opens/closes the shared confirm-dialog id on the global modal stack. */
  openConfirm: () => void;
  closeConfirm: () => void;
}

export interface AuditRestoreFlow {
  /** Non-null exactly while THIS surface owns a pending restore. */
  pendingEvent: AuditEventDto | null;
  plan: RestorePlanResponse | null;
  planChanged: boolean;
  errorMessage: string | null;
  isPlanning: boolean;
  isApplying: boolean;
  /** True while either half of the flow is in flight — disables the row controls. */
  isBusy: boolean;
  requestRestore: (event: AuditEventDto) => void;
  dismissRestore: () => void;
  confirmRestore: () => void;
}

export function useAuditRestoreFlow({
  entityType,
  entityId,
  active,
  openConfirm,
  closeConfirm,
}: UseAuditRestoreFlowOptions): AuditRestoreFlow {
  // Two mutations, not one: the plan request and the apply request have separate
  // pending states, and the dialog shows a different thing for each.
  const planRestore = useAuditRestore(entityType, entityId);
  const applyRestore = useAuditRestore(entityType, entityId);

  const [pendingEvent, setPendingEvent] = useState<AuditEventDto | null>(null);
  const [plan, setPlan] = useState<RestorePlanResponse | null>(null);
  const [planChanged, setPlanChanged] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  /** `mode: "plan"` — never writes. Its whole job is to fill the confirmation. */
  const loadPlan = useCallback(
    async (eventId: string): Promise<RestorePlanResponse | null> => {
      if (!entityId) return null;
      try {
        const response = await planRestore.mutateAsync({
          entityType,
          entityId,
          eventId,
          mode: "plan",
        });
        if (isRestoreApplied(response)) {
          setErrorMessage("The server applied the restore without asking. Reload the history.");
          return null;
        }
        setPlan(response);
        return response;
      } catch (error) {
        setErrorMessage(errorMessageOf(error));
        return null;
      }
    },
    [entityId, entityType, planRestore]
  );

  const requestRestore = useCallback(
    (event: AuditEventDto) => {
      setPendingEvent(event);
      setPlan(null);
      setPlanChanged(false);
      setErrorMessage(null);
      openConfirm();
      void loadPlan(event.eventId);
    },
    [loadPlan, openConfirm]
  );

  const dismissRestore = useCallback(() => {
    closeConfirm();
    setPendingEvent(null);
    setPlan(null);
    setPlanChanged(false);
    setErrorMessage(null);
  }, [closeConfirm]);

  // The surface going away (modal closed, tab switched) must take the
  // confirmation with it — otherwise a half-read confirmation is left floating
  // with no feed behind it.
  useEffect(() => {
    if (active || !pendingEvent) return;
    dismissRestore();
  }, [active, pendingEvent, dismissRestore]);

  const applyPlan = useCallback(async () => {
    if (!entityId || !plan) return;
    setErrorMessage(null);
    try {
      const response = await applyRestore.mutateAsync({
        entityType,
        entityId,
        eventId: plan.eventId,
        mode: "apply",
        planHash: plan.planHash,
        fields: plan.fields.map((field) => field.field),
      });
      if (!isRestoreApplied(response)) {
        // The route answered with a plan instead of applying — treat it exactly
        // like a hash mismatch: show the new plan, make the user confirm again.
        setPlan(response);
        setPlanChanged(true);
        return;
      }
      const summary = restoreSummaryLabel(plan.kind, response.fieldsWritten);
      const description = restoreCautionText(response);
      if (description) {
        toast.success(summary, { description });
      } else {
        toast.success(summary);
      }
      dismissRestore();
    } catch (error) {
      if (error instanceof AuditPlanStaleError) {
        // The record moved while the dialog was open. NEVER retry silently:
        // re-fetch the plan and tell the user it changed.
        setPlanChanged(true);
        setPlan(null);
        await loadPlan(plan.eventId);
        return;
      }
      setErrorMessage(errorMessageOf(error));
    }
  }, [applyRestore, dismissRestore, entityId, entityType, loadPlan, plan]);

  const confirmRestore = useCallback(() => {
    void applyPlan();
  }, [applyPlan]);

  return {
    pendingEvent,
    plan,
    planChanged,
    errorMessage,
    isPlanning: planRestore.isPending,
    isApplying: applyRestore.isPending,
    isBusy: planRestore.isPending || applyRestore.isPending,
    requestRestore,
    dismissRestore,
    confirmRestore,
  };
}
